import { STAFF_PATHS } from '../utils/staffPaths';
import TenantEngagement from './TenantEngagement';
import { Tabs, Tab } from '@mui/material';
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAdminToken, removeAdminToken } from "../services/authService";
import { useTheme } from "../ThemeContext";
import './Tenants.css'
import { registrationRange, tenantLicenseLabel } from './tenantAnalytics';
import { registrationCalendar, calendarBands, CALENDARS, holidaysForYear, weekdayName } from './registrationCalendar';
import { Bar, Pie, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  useMediaQuery,
  Chip,
  Avatar,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import FolderIcon from "@mui/icons-material/Folder";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableChartIcon from "@mui/icons-material/TableChart";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PhonelinkEraseIcon from "@mui/icons-material/PhonelinkErase";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// Canvas charts need resolved colors; CSS var() strings cannot be painted by Chart.js.
const chartColor = name => ({ chart }) => getComputedStyle(chart.canvas).getPropertyValue(`--v2-${name}`).trim();
const pieColors = ['accent', 'success', 'warning', 'danger', 'text-secondary', 'text-muted'];

const TenantChart = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [chartType, setChartType] = useState("line");
  const [pieMetric, setPieMetric] = useState('plans');
  const [datePreset, setDatePreset] = useState('year');
  const [dateRange, setDateRange] = useState(() => registrationRange('year'));
  const invalidDateRange = Boolean(dateRange.start && dateRange.end && dateRange.start > dateRange.end);
  const [excludeWeekends, setExcludeWeekends] = useState(false);
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  const [holidayCalendar, setHolidayCalendar] = useState('federal');
  const [chartGrain, setChartGrain] = useState('day');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendar = useMemo(() => registrationCalendar(tenants, dateRange, {
    excludeWeekends, excludeHolidays, calendar: holidayCalendar, grain: chartGrain,
  }), [tenants, dateRange, excludeWeekends, excludeHolidays, holidayCalendar, chartGrain]);
  const chartTenants = calendar.tenants;
  const holidayDates = useMemo(() => {
    if (calendar.error) return [];
    const entries = [];
    for (let year = Number(calendar.start.slice(0, 4)); year <= Number(calendar.end.slice(0, 4)); year++) {
      entries.push(...Object.entries(holidaysForYear(year, holidayCalendar)).filter(([date]) => date >= calendar.start && date <= calendar.end));
    }
    return entries.sort(([a], [b]) => a.localeCompare(b));
  }, [calendar.start, calendar.end, calendar.error, holidayCalendar]);
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'card'
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');

  // Tenant management states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Tenant users states
  const [viewingTenantUsers, setViewingTenantUsers] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [usersPagination, setUsersPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Create user states
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    is_admin: false,
    contact_number: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);

  // Update password states
  const [updatePasswordDialogOpen, setUpdatePasswordDialogOpen] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [updatePasswordLoading, setUpdatePasswordLoading] = useState(false);

  // Edit user states
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserLoading, setEditUserLoading] = useState(false);

  // Update license states
  const [updateLicenseDialogOpen, setUpdateLicenseDialogOpen] = useState(false);
  const [selectedTenantForLicense, setSelectedTenantForLicense] = useState(null);
  const [licenseType, setLicenseType] = useState('community');
  const [licenseExpiresAt, setLicenseExpiresAt] = useState('');
  const [updateLicenseLoading, setUpdateLicenseLoading] = useState(false);

  // Create tenant form states
  const [newTenant, setNewTenant] = useState({
    tenant_name: '',
    tenant_domain: '',
    address: '',
    website: '',
    logo_url: '',
    status: 'active'
  });

  // Fetch tenants
  const fetchTenants = async () => {
    try {
      const token = getAdminToken();
      const res = await fetch("/s1/admin/tenants", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === "Token has expired") {
          removeAdminToken();
          navigate(STAFF_PATHS.login);
        } else {
          setError(data.error || "Unexpected error occurred");
        }
      } else {
        setTenants(data);
      }
    } catch (err) {
      setError(err.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [navigate]);

  // Tenant management functions
  const handleCreateTenant = async () => {
    try {
      const token = getAdminToken();
      const res = await fetch("/s1/admin/tenants", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newTenant),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to create tenant");
      }

      setSnackbar({ open: true, message: "Tenant created successfully", severity: "success" });
      setCreateDialogOpen(false);
      setNewTenant({
        tenant_name: '',
        tenant_domain: '',
        address: '',
        website: '',
        logo_url: '',
        status: 'active'
      });
      fetchTenants();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleTenantAction = async (tenantId, action) => {
    try {
      const token = getAdminToken();
      let endpoint = '';
      let method = 'PUT';

      switch (action) {
        case 'archive':
          endpoint = `/s1/admin/tenants/${tenantId}/archive`;
          break;
        case 'disable':
          endpoint = `/s1/admin/tenants/${tenantId}/disable`;
          break;
        case 'enable':
          endpoint = `/s1/admin/tenants/${tenantId}/enable`;
          break;
        case 'delete':
          endpoint = `/s1/admin/tenants/${tenantId}`;
          method = 'DELETE';
          break;
        default:
          throw new Error('Invalid action');
      }

      const res = await fetch(endpoint, {
        method,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || `Failed to ${action} tenant`);
      }

      setSnackbar({
        open: true,
        message: `Tenant ${action}d successfully`,
        severity: "success"
      });
      setActionDialogOpen(false);
      fetchTenants();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    }
  };

  const openActionDialog = (tenant, action) => {
    setSelectedTenant(tenant);
    setActionType(action);
    setActionDialogOpen(true);
  };

  // Fetch users for a tenant
  const fetchTenantUsers = async (tenantId, page = 1) => {
    try {
      setUsersLoading(true);
      setUsersError('');
      const token = getAdminToken();
      const res = await fetch(`/s1/admin/tenants/${tenantId}/users?page=${page}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.error === "Token has expired") {
          removeAdminToken();
          navigate(STAFF_PATHS.login);
        } else {
          setUsersError(data.error || "Failed to fetch users");
        }
      } else {
        setTenantUsers(data.data || []);
        setUsersPagination(data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        });
      }
    } catch (err) {
      setUsersError(err.message || "Something went wrong!");
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle tenant click to view users
  const handleTenantClick = (tenant) => {
    setViewingTenantUsers(tenant);
    setUsersPage(1);
    fetchTenantUsers(tenant.id, 1);
  };

  // Handle back to tenants list
  const handleBackToTenants = () => {
    setViewingTenantUsers(null);
    setTenantUsers([]);
    setUsersPage(1);
    setUsersError('');
  };

  // Handle users page change
  const handleUsersPageChange = (event, newPage) => {
    setUsersPage(newPage + 1);
    fetchTenantUsers(viewingTenantUsers.id, newPage + 1);
  };

  // Handle create user
  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.password) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }

    if (newUser.password.length < 6) {
      setSnackbar({ open: true, message: 'Password must be at least 6 characters', severity: 'error' });
      return;
    }

    try {
      setCreateUserLoading(true);
      const token = getAdminToken();
      const res = await fetch(`/s1/admin/tenants/${viewingTenantUsers.id}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: newUser.username,
          email: newUser.email,
          password: newUser.password,
          is_admin: newUser.is_admin,
          contact_number: newUser.contact_number || null
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.error === "Token has expired") {
          removeAdminToken();
          navigate(STAFF_PATHS.login);
        } else {
          setSnackbar({ open: true, message: data.error || 'Failed to create user', severity: 'error' });
        }
      } else {
        setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
        setCreateUserDialogOpen(false);
        setNewUser({
          username: '',
          email: '',
          password: '',
          is_admin: false,
          contact_number: ''
        });
        // Refresh users list
        fetchTenantUsers(viewingTenantUsers.id, usersPage);
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Something went wrong!', severity: 'error' });
    } finally {
      setCreateUserLoading(false);
    }
  };

  // Handle update password
  const handleUpdatePassword = async () => {
    if (!newPassword) {
      setSnackbar({ open: true, message: 'Password is required', severity: 'error' });
      return;
    }

    if (newPassword.length < 6) {
      setSnackbar({ open: true, message: 'Password must be at least 6 characters', severity: 'error' });
      return;
    }

    try {
      setUpdatePasswordLoading(true);
      const token = getAdminToken();
      const res = await fetch(`/s1/admin/tenants/${viewingTenantUsers.id}/users/${selectedUserForPassword.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password: newPassword }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.error === "Token has expired") {
          removeAdminToken();
          navigate(STAFF_PATHS.login);
        } else {
          setSnackbar({ open: true, message: data.error || 'Failed to update password', severity: 'error' });
        }
      } else {
        setSnackbar({ open: true, message: 'Password updated successfully', severity: 'success' });
        setUpdatePasswordDialogOpen(false);
        setSelectedUserForPassword(null);
        setNewPassword('');
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Something went wrong!', severity: 'error' });
    } finally {
      setUpdatePasswordLoading(false);
    }
  };

  // Open update password dialog
  const openUpdatePasswordDialog = (user) => {
    setSelectedUserForPassword(user);
    setNewPassword('');
    setUpdatePasswordDialogOpen(true);
  };

  // Handle edit user click
  const handleEditUserClick = (user) => {
    setEditingUser({
      ...user,
      contact_number: user.contact_number || ''
    });
    setEditUserDialogOpen(true);
  };

  // Handle save user changes
  const handleSaveUserChanges = async () => {
    if (!editingUser.username || !editingUser.email) {
      setSnackbar({ open: true, message: 'Username and email are required', severity: 'error' });
      return;
    }

    try {
      setEditUserLoading(true);
      const token = getAdminToken();
      const res = await fetch(`/s1/admin/tenants/${viewingTenantUsers.id}/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: editingUser.username,
          email: editingUser.email,
          is_admin: editingUser.is_admin,
          contact_number: editingUser.contact_number
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.error === "Token has expired") {
          removeAdminToken();
          navigate(STAFF_PATHS.login);
        } else {
          setSnackbar({ open: true, message: data.error || 'Failed to update user', severity: 'error' });
        }
      } else {
        setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
        setEditUserDialogOpen(false);
        setEditingUser(null);
        // Refresh users list
        fetchTenantUsers(viewingTenantUsers.id, usersPage);
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Something went wrong!', severity: 'error' });
    } finally {
      setEditUserLoading(false);
    }
  };

  // Handle toggle user status (ban/unban)
  const handleToggleUserStatus = async (user) => {
    const newStatus = user.is_deleted ? 'active' : 'banned';
    const actionText = user.is_deleted ? 'activate' : 'deactivate';

    if (!window.confirm(`Are you sure you want to ${actionText} this user?`)) {
      return;
    }

    try {
      const token = getAdminToken();
      const res = await fetch(`/s1/admin/tenants/${viewingTenantUsers.id}/users/${user.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.error === "Token has expired") {
          removeAdminToken();
          navigate(STAFF_PATHS.login);
        } else {
          setSnackbar({ open: true, message: data.error || `Failed to ${actionText} user`, severity: 'error' });
        }
      } else {
        setSnackbar({ open: true, message: `User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`, severity: 'success' });
        // Refresh users list
        fetchTenantUsers(viewingTenantUsers.id, usersPage);
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Something went wrong!', severity: 'error' });
    }
  };

  // Platform admin: remove a user's authenticator (TOTP) MFA — e.g. they lost their
  // device and their recovery codes. Reverts them to the emailed-code login.
  const handleResetUserMfa = async (user) => {
    if (!window.confirm(`Remove ${user.username || user.email}'s authenticator app? They will sign in with an emailed code instead.`)) {
      return;
    }
    try {
      const token = getAdminToken();
      const res = await fetch(`/s1/admin/tenants/${viewingTenantUsers.id}/users/${user.id}/mfa/reset`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.error === "Token has expired") {
          removeAdminToken();
          navigate(STAFF_PATHS.login);
        } else {
          setSnackbar({ open: true, message: data.error || 'Failed to reset MFA', severity: 'error' });
        }
      } else {
        setSnackbar({ open: true, message: 'Authenticator MFA removed for this user', severity: 'success' });
        fetchTenantUsers(viewingTenantUsers.id, usersPage);
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Something went wrong!', severity: 'error' });
    }
  };

  // Handle update license
  const handleUpdateLicense = async () => {
    if (!selectedTenantForLicense) return;

    if (licenseType === 'pro' && !licenseExpiresAt) {
      setSnackbar({ open: true, message: 'Expiration date is required for Pro license', severity: 'error' });
      return;
    }

    try {
      setUpdateLicenseLoading(true);
      const token = getAdminToken();
      const res = await fetch(`/s1/admin/tenants/${selectedTenantForLicense.id}/license`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          license_type: licenseType,
          expires_at: licenseType === 'pro' ? licenseExpiresAt : null
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.error === "Token has expired") {
          removeAdminToken();
          navigate(STAFF_PATHS.login);
        } else {
          setSnackbar({ open: true, message: data.error || 'Failed to update license', severity: 'error' });
        }
      } else {
        setSnackbar({ open: true, message: 'License updated successfully', severity: 'success' });
        setUpdateLicenseDialogOpen(false);
        setSelectedTenantForLicense(null);
        setLicenseType('community');
        setLicenseExpiresAt('');
        // Refresh tenants list
        fetchTenants();
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Something went wrong!', severity: 'error' });
    } finally {
      setUpdateLicenseLoading(false);
    }
  };

  // Open update license dialog
  const openUpdateLicenseDialog = (tenant) => {
    setSelectedTenantForLicense(tenant);
    setLicenseType(tenant.license_type || 'community');
    setLicenseExpiresAt(tenant.license_expires_at ? tenant.license_expires_at.split('T')[0] : '');
    setUpdateLicenseDialogOpen(true);
  };

  // Summary
  const summary = useMemo(() => {
    const totalTenants = tenants.length;
    const totalOrganizations = tenants.reduce(
      (acc, t) => acc + t.organizations_count,
      0
    );
    const totalFiles = tenants.reduce((acc, t) => acc + t.files_count, 0);
    return { totalTenants, totalOrganizations, totalFiles };
  }, [tenants]);

  // Chart data
  const barData = useMemo(() => {
    const countsByDate = Object.fromEntries(calendar.buckets.map(bucket => [bucket.date, bucket]));
    const labels = calendar.buckets.map(bucket => bucket.date);
    return {
      labels,
      datasets: [
        {
          label: "Tenants",
          data: labels.map(d => countsByDate[d].tenants),
          backgroundColor: chartColor('accent'),
        },
        {
          label: "Organizations",
          data: labels.map(d => countsByDate[d].organizations),
          backgroundColor: chartColor('success'),
        },
        {
          label: "Files",
          data: labels.map(d => countsByDate[d].files),
          backgroundColor: chartColor('warning'),
        },
      ],
    };
  }, [calendar.buckets]);

  const pieData = useMemo(() => {
    const values = pieMetric === 'plans'
      ? Object.entries(chartTenants.reduce((counts, tenant) => {
        const label = tenantLicenseLabel(tenant);
        counts[label] = (counts[label] || 0) + 1;
        return counts;
      }, {})).sort(([a], [b]) => a.localeCompare(b))
      : chartTenants.map(tenant => [tenant.name, Number((pieMetric === 'organizations' ? tenant.organizations_count : tenant.files_count) || 0)])
        .filter(([, count]) => count > 0);
    return {
      labels: values.map(([label]) => label),
      datasets: [
        {
          label: pieMetric === 'plans' ? 'Tenants' : pieMetric === 'organizations' ? 'Organizations' : 'Files',
          data: values.map(([, count]) => count),
          backgroundColor: context => chartColor(pieColors[context.dataIndex % pieColors.length])(context),
          borderColor: chartColor('surface'),
          borderWidth: 1,
        },
      ],
    };
  }, [chartTenants, pieMetric]);
  const pieTotal = pieData.datasets[0].data.reduce((total, count) => total + count, 0);
  const pieTitle = pieMetric === 'plans' ? 'Tenant plan distribution'
    : pieMetric === 'organizations' ? 'Organizations by tenant' : 'Files by tenant';

  const lineData = useMemo(() => ({
    labels: barData.labels,
    datasets: barData.datasets.map(dataset => ({
      ...dataset,
      borderColor: dataset.backgroundColor,
      fill: false,
      tension: 0.25,
    })),
  }), [barData]);

  const timelineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: isMobile ? 'bottom' : 'top', labels: { color: chartColor('text-secondary') } },
      registrationCalendarBands: { days: chartGrain === 'day' ? calendar.dates : [] },
      tooltip: { callbacks: {
        title: items => {
          const bucket = calendar.buckets[items[0]?.dataIndex];
          if (!bucket) return '';
          return chartGrain === 'day' ? `${bucket.days[0].weekday}, ${bucket.date}` : chartGrain === 'week' ? `Week of ${bucket.date}` : bucket.date.slice(0, 7);
        },
        afterTitle: items => {
          const bucket = calendar.buckets[items[0]?.dataIndex];
          if (!bucket) return [];
          const notes = chartGrain === 'day' ? bucket.days[0].holidays : [`${bucket.days.length} included calendar days`];
          return [...notes, ...(bucket.days.some(day => day.incomplete) ? ['Includes today — incomplete (Central time)'] : [])];
        },
      } },
    },
    scales: {
      y: { beginAtZero: true, ticks: { color: chartColor('text-secondary'), precision: 0 }, grid: { color: chartColor('border') } },
      x: { ticks: { color: chartColor('text-secondary'), maxTicksLimit: isMobile ? 6 : 12 }, grid: { color: chartColor('border') } },
    },
  };

  // Filtered tenants
  const filteredTenants = useMemo(() => {
    if (!searchTerm) return tenants;
    return tenants.filter(
      t =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.domain.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [tenants, searchTerm]);

  const columns = [
    { field: "id", headerName: "#", width: 70 },
    {
      field: "name",
      headerName: "Tenant Name",
      width: 200,
      renderCell: (params) => (
        <Box
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleTenantClick(params.row);
          }}
        >
          {params.value}
        </Box>
      )
    },
    { field: "domain", headerName: "Domain", width: 180 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const status = params.value;
        const getStatusColor = (status) => {
          switch (status) {
            case 'active': return 'success';
            case 'disabled': return 'warning';
            case 'archived': return 'default';
            default: return 'default';
          }
        };
        return (
          <Chip
            label={status}
            color={getStatusColor(status)}
            size="small"
            variant="outlined"
          />
        );
      }
    },
    {
      field: "license_type",
      headerName: "License",
      width: 175,
      renderCell: (params) => {
        const licenseType = params.value || 'community';
        const isPro = licenseType === 'pro';
        return (
          <Chip
            label={tenantLicenseLabel(params.row)}
            color={isPro ? (params.row.license_source === 'trial' ? 'warning' : 'primary') : 'default'}
            size="small"
            variant={isPro ? 'filled' : 'outlined'}
          />
        );
      }
    },
    { field: "created_at", headerName: "Created At", width: 120 },
    {
      field: "users_count",
      headerName: "Users",
      width: 80,
      renderCell: (params) => (
        <Box
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            fontWeight: 'bold',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleTenantClick(params.row);
          }}
        >
          {params.value || 0}
        </Box>
      )
    },
    { field: "organizations_count", headerName: "Organizations", width: 120 },
    { field: "files_count", headerName: "Files", width: 80 },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      sortable: false,
      renderCell: (params) => {
        const tenant = params.row;
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openUpdateLicenseDialog(tenant);
              }}
              title="Update License"
              color="primary"
            >
              <WorkspacePremiumIcon fontSize="small" />
            </IconButton>
            {tenant.status === 'active' && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openActionDialog(tenant, 'disable');
                  }}
                  title="Disable"
                >
                  <BlockIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openActionDialog(tenant, 'archive');
                  }}
                  title="Archive"
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </>
            )}
            {tenant.status === 'disabled' && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openActionDialog(tenant, 'enable');
                }}
                title="Enable"
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openActionDialog(tenant, 'delete');
              }}
              title="Delete"
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      }
    }
  ];

  const rows = filteredTenants.map((tenant, index) => ({
    id: index + 1,
    ...tenant,
    created_at: tenant.created_at.split("T")[0],
  }));

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "var(--v2-bg)",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );

  return (
    <Box
      className={isDarkMode ? "bg-dark text-light" : ''}
      sx={{
        p: 0,
        backgroundColor: "var(--v2-bg)",
        color: "var(--v2-text)",
        minHeight: 0,
      }}
    >
      {error && (
        <Typography color="error" variant="h6" mb={2}>
          {error}
        </Typography>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        {[
          {
            icon: <PeopleIcon sx={{ fontSize: 40 }} />,
            label: "Total Tenants",
            value: summary.totalTenants,
            colors: ["#2196f3", "#21cbf3"],
          },
          {
            icon: <BusinessIcon sx={{ fontSize: 40 }} />,
            label: "Total Organizations",
            value: summary.totalOrganizations,
            colors: ["#f50057", "#ff4081"],
          },
          {
            icon: <FolderIcon sx={{ fontSize: 40 }} />,
            label: "Total Files",
            value: summary.totalFiles,
            colors: ["#ff9800", "#ffc107"],
          },
        ].map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
            <Card
              sx={{
                background: "var(--v2-surface)",
                color: "var(--v2-text)",
                borderRadius: 2,
                boxShadow: 3,
                height: '100%',
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
                <Avatar sx={{ bgcolor: "var(--v2-accent-subtle)", color: "var(--v2-accent)", width: 46, height: 46 }}>
                  {card.icon}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {card.label}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {card.value.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Chart Section */}
      <Card sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        borderRadius: 2,
        backgroundColor: "var(--v2-surface)",
        boxShadow: 3
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={2}>
          <Typography style={{ color: "var(--v2-text)" }} variant="h6" sx={{ mb: { xs: 1, sm: 0 } }}>Analytics Overview</Typography>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={(e, newType) => newType && setChartType(newType)}
            size={isMobile ? "small" : "medium"}
          >
            <ToggleButton value="bar" sx={{
              color: "var(--v2-text-secondary)",
              '&.Mui-selected': { backgroundColor: "var(--v2-accent-subtle)" }
            }}>
              Bar
            </ToggleButton>
            <ToggleButton value="pie" sx={{
              color: "var(--v2-text-secondary)",
              '&.Mui-selected': { backgroundColor: "var(--v2-accent-subtle)" }
            }}>
              Pie
            </ToggleButton>
            <ToggleButton value="line" sx={{
              color: "var(--v2-text-secondary)",
              '&.Mui-selected': { backgroundColor: "var(--v2-accent-subtle)" }
            }}>
              Line
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Each control takes a full row on a phone and sits side by side once there is room. */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, '& > *': { flex: { xs: '1 1 100%', sm: '0 1 auto' } } }}>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="tenant-chart-range-label">Registration range</InputLabel>
            <Select labelId="tenant-chart-range-label" label="Registration range" value={datePreset}
              onChange={event => {
                setDatePreset(event.target.value);
                if (event.target.value !== 'custom') setDateRange(registrationRange(event.target.value));
              }}>
              <MenuItem value="all">All time</MenuItem>
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="year">This year (1 year)</MenuItem>
              <MenuItem value="2years">Last 2 years</MenuItem>
              <MenuItem value="custom">Custom range</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Start date" type="date" size="small" value={dateRange.start}
            InputLabelProps={{ shrink: true }} error={invalidDateRange}
            onChange={event => { setDatePreset('custom'); setDateRange({ ...dateRange, start: event.target.value }); }} />
          <TextField label="End date" type="date" size="small" value={dateRange.end}
            InputLabelProps={{ shrink: true }} error={invalidDateRange}
            onChange={event => { setDatePreset('custom'); setDateRange({ ...dateRange, end: event.target.value }); }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="tenant-chart-grain">Group dates</InputLabel>
            <Select labelId="tenant-chart-grain" label="Group dates" value={chartGrain} onChange={event => setChartGrain(event.target.value)}>
              <MenuItem value="day">Daily</MenuItem><MenuItem value="week">Weekly</MenuItem><MenuItem value="month">Monthly</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 210 }}>
            <InputLabel id="tenant-chart-calendar">Holiday reference calendar</InputLabel>
            <Select labelId="tenant-chart-calendar" label="Holiday reference calendar" value={holidayCalendar} onChange={event => setHolidayCalendar(event.target.value)}>
              {Object.entries(CALENDARS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel control={<Checkbox checked={excludeWeekends} onChange={event => setExcludeWeekends(event.target.checked)} />} label="Exclude weekends" />
          <FormControlLabel control={<Checkbox checked={excludeHolidays} onChange={event => setExcludeHolidays(event.target.checked)} />} label="Exclude observed holidays" />
          <Button onClick={() => setCalendarOpen(true)}>View holiday calendar</Button>
        </Box>
        <Typography variant="body2" sx={{ color: 'var(--v2-text-secondary)', mb: 1 }} role="status">
          Texas Central time (America/Chicago) · {calendar.dates.length} days included · {chartTenants.length} tenant registrations
          {excludeWeekends || excludeHolidays ? ' · Calendar filters applied' : ' · All days included'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--v2-text-secondary)', mb: 2 }}>
          Zero-registration days remain visible. Gray shading marks weekends; gold marks holidays in daily charts. Today is incomplete; future dates are excluded.
          Organization and file counts are current totals for tenants registered on included dates, not activity during the range. Summary cards and the tenant list show all tenants.
          Holiday dates use the selected reference schedule, not confirmed company closures. All time starts at the earliest dated tenant registration.
        </Typography>
        <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Holiday reference calendar</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>{CALENDARS[holidayCalendar]} · {calendar.start} through {calendar.end}</Typography>
            <Typography variant="body2" gutterBottom>
              Federal fixed-date holidays use Friday/Monday substitutes when they fall on weekends. Texas state holidays have no substitute weekday; partial-staffing dates are included, optional religious holidays are not. Neither schedule confirms private-company closures.
              The holiday filter removes listed actual and observed dates only when enabled.
            </Typography>
            <Box component="ul">{holidayDates.map(([date, names]) => <li key={date}>{weekdayName(date)}, {date}: {names.join('; ')}</li>)}</Box>
            {!holidayDates.length && <Typography>No listed holidays in this reporting period.</Typography>}
            <Typography variant="body2"><a href="https://www.opm.gov/policy-data-oversight/pay-leave/federal-holidays/" target="_blank" rel="noreferrer">Federal schedule</a>{' · '}<a href="https://www.tsl.texas.gov/ref/abouttx/holidays" target="_blank" rel="noreferrer">Texas state schedule</a></Typography>
          </DialogContent>
          <DialogActions><Button onClick={() => setCalendarOpen(false)}>Close calendar</Button></DialogActions>
        </Dialog>
        {invalidDateRange || calendar.error ? <Alert severity="error">{calendar.error || 'Start date must be on or before end date.'}</Alert>
          : calendar.dates.length === 0 ? <Alert severity="info">No calendar dates remain in this range after filters and the exclusion of future dates.</Alert> : (
        <Box sx={{ height: chartType === 'pie' ? 'auto' : isMobile ? 250 : 350, mt: 2 }}>
          {chartType === "bar" && (
            <Bar
              data={barData}
              plugins={[calendarBands]}
              options={timelineOptions}
            />
          )}
          {chartType === "pie" && (
            <Box>
              <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
                <InputLabel id="tenant-pie-metric-label">Pie chart shows</InputLabel>
                <Select labelId="tenant-pie-metric-label" label="Pie chart shows" value={pieMetric}
                  onChange={event => setPieMetric(event.target.value)}>
                  <MenuItem value="plans">Tenant plans</MenuItem>
                  <MenuItem value="organizations">Organizations by tenant</MenuItem>
                  <MenuItem value="files">Files by tenant</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="subtitle1" sx={{ color: 'var(--v2-text)', fontWeight: 600 }}>{pieTitle}</Typography>
              <Typography variant="body2" sx={{ color: 'var(--v2-text-secondary)', mb: 2 }}>
                {pieMetric === 'plans'
                  ? 'Each slice counts tenants on a current plan, including separate trial, paid and manual Pro grants.'
                  : `Each slice shows one tenant’s share of the current ${pieMetric === 'organizations' ? 'organization' : 'file'} total.`}
                {' '}Only tenants registered on included dates in the selected range are included.
              </Typography>
              {pieTotal === 0 ? <Alert severity="info">No {pieMetric === 'organizations' ? 'organizations' : 'files'} for tenants in this date range.</Alert> : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 3, alignItems: 'center', maxWidth: 820, mx: 'auto' }}>
              <Box sx={{ position: 'relative', height: { xs: 260, sm: 320 }, width: '100%', maxWidth: 360, minWidth: 0, mx: 'auto' }}>
                <Pie
                  role="img"
                  aria-label={pieTitle}
                  data={pieData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: 8 },
                    plugins: {
                      legend: { display: false },
                      tooltip: { callbacks: {
                        label: context => `${context.label}: ${context.raw.toLocaleString()} ${pieData.datasets[0].label.toLowerCase()} (${(context.raw / pieTotal * 100).toFixed(1)}%)`,
                      } },
                    }
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: 'var(--v2-text)', fontWeight: 600, mb: 1 }}>
                  Total: {pieTotal.toLocaleString()} {pieData.datasets[0].label.toLowerCase()}
                </Typography>
                <Box component="ul" aria-label={`${pieTitle} legend`} sx={{ listStyle: 'none', m: 0, p: 0, maxHeight: 240, overflowY: 'auto' }}>
                  {pieData.labels.map((label, index) => (
                    <Box component="li" key={index} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', py: 0.75, color: 'var(--v2-text)' }}>
                      <Box aria-hidden="true" component="span" sx={{ width: 10, height: 10, flexShrink: 0, borderRadius: '50%', backgroundColor: `var(--v2-${pieColors[index % pieColors.length]})` }} />
                      <Typography component="span" variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{label}</Typography>
                      <Typography component="span" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {pieData.datasets[0].data[index].toLocaleString()} ({(pieData.datasets[0].data[index] / pieTotal * 100).toFixed(1)}%)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              </Box>
              )}
            </Box>
          )}
          {chartType === "line" && (
            <Line
              data={lineData}
              plugins={[calendarBands]}
              options={timelineOptions}
            />
          )}
        </Box>
        )}
        {!invalidDateRange && calendar.dates.length > 0 && <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2">Average tenant registrations by weekday</Typography>
          <Typography variant="caption">Included, completed days only; today is excluded from averages. A dash means no eligible days.</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {calendar.weekdayAverages.map(item => <Chip key={item.weekday} label={`${item.weekday}: ${item.average === null ? '—' : item.average.toFixed(2)} (${item.days} days)`} />)}
          </Box>
        </Box>}
      </Card>

      {/* Tenants Section or Users Section */}
      {viewingTenantUsers ? (
        <Card sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          backgroundColor: "var(--v2-surface)",
          boxShadow: 3
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton
                onClick={handleBackToTenants}
                sx={{ color: "var(--v2-text)" }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography style={{ color: "var(--v2-text)" }} variant="h6">
                  Users - {viewingTenantUsers.name}
                </Typography>
                <Typography style={{ color: "var(--v2-text-secondary)" }} variant="body2">
                  {viewingTenantUsers.domain}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} mt={isMobile ? 1 : 0}>
              <Chip
                label={`Total: ${usersPagination.totalItems} users`}
                color="primary"
                variant="outlined"
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateUserDialogOpen(true)}
                size="small"
              >
                Create User
              </Button>
            </Box>
          </Box>

          {usersError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {usersError}
            </Alert>
          )}

          {usersLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={tenantUsers.map((user, index) => ({
                    id: user.id || index + 1,
                    ...user,
                    created_at: user.created_at ? user.created_at.split('T')[0] : ''
                  }))}
                  columns={[
                    { field: 'id', headerName: 'ID', width: 80 },
                    {
                      field: 'username',
                      headerName: 'Username',
                      width: 150,
                      renderCell: (params) => (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" />
                          {params.value}
                        </Box>
                      )
                    },
                    {
                      field: 'email',
                      headerName: 'Email',
                      width: 250,
                      renderCell: (params) => (
                        <Box display="flex" alignItems="center" gap={1}>
                          <EmailIcon fontSize="small" />
                          {params.value}
                        </Box>
                      )
                    },
                    {
                      field: 'contact_number',
                      headerName: 'Phone',
                      width: 180,
                      renderCell: (params) => (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIcon fontSize="small" />
                          {params.value || '-'}
                        </Box>
                      )
                    },
                    {
                      field: 'is_admin',
                      headerName: 'Role',
                      width: 120,
                      renderCell: (params) => (
                        <Chip
                          label={params.value ? 'Admin' : 'User'}
                          color={params.value ? 'warning' : 'default'}
                          size="small"
                        />
                      )
                    },
                    {
                      field: 'banned',
                      headerName: 'Status',
                      width: 120,
                      renderCell: (params) => (
                        <Chip
                          label={params.row.is_deleted ? 'Deactivated' : 'Active'}
                          color={params.row.is_deleted ? 'error' : 'success'}
                          size="small"
                          variant="outlined"
                        />
                      )
                    },
                    { field: 'created_at', headerName: 'Created At', width: 120 },
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      width: 180,
                      sortable: false,
                      renderCell: (params) => (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditUserClick(params.row);
                            }}
                            title="Edit User"
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openUpdatePasswordDialog(params.row);
                            }}
                            title="Update Password"
                            color="secondary"
                          >
                            <LockIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleUserStatus(params.row);
                            }}
                            title={params.row.is_deleted ? "Activate User" : "Deactivate User"}
                            color={params.row.is_deleted ? "success" : "error"}
                          >
                            {params.row.is_deleted ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                          </IconButton>
                          {params.row.totp_enabled && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResetUserMfa(params.row);
                              }}
                              title="Remove Authenticator (2FA) — use if they lost their device and recovery codes"
                              color="warning"
                            >
                              <PhonelinkEraseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )
                    }
                  ]}
                  pageSize={10}
                  rowsPerPageOptions={[10]}
                  disableSelectionOnClick
                  pagination
                  rowCount={usersPagination.totalItems}
                  page={usersPage - 1}
                  onPageChange={handleUsersPageChange}
                  sx={{
                    backgroundColor: "var(--v2-surface)",
                    color: "var(--v2-text)",
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: "var(--v2-surface)",
                      color: "var(--v2-text)",
                      borderBottom: `1px solid ${"var(--v2-border)"}`,
                    },
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: "var(--v2-surface)",
                      color: "var(--v2-text)",
                      '&:focus': {
                        outline: 'none',
                      },
                      '&:focus-within': {
                        outline: 'none',
                      },
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 600,
                      color: "var(--v2-text)",
                    },
                    '& .MuiDataGrid-columnHeaderTitleContainer': {
                      color: "var(--v2-text)",
                    },
                    '& .MuiDataGrid-iconButtonContainer': {
                      visibility: 'visible',
                      '& .MuiIconButton-root': {
                        color: "var(--v2-text)",
                      },
                    },
                    '& .MuiDataGrid-sortIcon': {
                      color: "var(--v2-text)",
                    },
                    '& .MuiDataGrid-menuIconButton': {
                      color: "var(--v2-text)",
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: "1px solid var(--v2-border)",
                      color: "var(--v2-text)",
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: "var(--v2-accent-subtle)",
                    },
                    '& .MuiTablePagination-root': {
                      color: "var(--v2-text)",
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      color: "var(--v2-text)",
                    },
                    '& .MuiIconButton-root': {
                      color: "var(--v2-text)",
                    },
                  }}
                />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Typography style={{ color: "var(--v2-text)" }} variant="body2">
                  Showing {((usersPage - 1) * usersPagination.itemsPerPage) + 1} to {Math.min(usersPage * usersPagination.itemsPerPage, usersPagination.totalItems)} of {usersPagination.totalItems} users
                </Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <Button
                    disabled={usersPage === 1}
                    onClick={() => {
                      const newPage = usersPage - 1;
                      setUsersPage(newPage);
                      fetchTenantUsers(viewingTenantUsers.id, newPage);
                    }}
                    variant="outlined"
                    size="small"
                  >
                    Previous
                  </Button>
                  <Typography style={{ color: "var(--v2-text)" }} variant="body2">
                    Page {usersPage} of {usersPagination.totalPages}
                  </Typography>
                  <Button
                    disabled={usersPage >= usersPagination.totalPages}
                    onClick={() => {
                      const newPage = usersPage + 1;
                      setUsersPage(newPage);
                      fetchTenantUsers(viewingTenantUsers.id, newPage);
                    }}
                    variant="outlined"
                    size="small"
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Card>
      ) : (
        <Card sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          backgroundColor: "var(--v2-surface)",
          boxShadow: 3
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={2}>
            <Typography style={{ color: "var(--v2-text)" }} variant="h6">Tenant Management</Typography>

            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mt={isMobile ? 1 : 0} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{ mr: { xs: 0, sm: 1 } }}
              >
                Create Tenant
              </Button>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                size="small"
              >
                <ToggleButton value="table" aria-label="Table view">
                  <TableChartIcon style={{ color: "var(--v2-text)" }} />
                </ToggleButton>
                <ToggleButton value="card" aria-label="Card view">
                  <ViewModuleIcon style={{ color: "var(--v2-text)" }} />
                </ToggleButton>
              </ToggleButtonGroup>

              <TextField
                placeholder="Search tenants..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                sx={{
                  ml: { xs: 0, sm: 1 },
                  width: { xs: '100%', sm: 250 },
                  '& .MuiInputBase-input': { color: "var(--v2-text)" }
                }}
                InputProps={{
                  startAdornment: <SearchIcon style={{ color: "var(--v2-text)" }} sx={{ color: 'text.secondary', mr: 1 }} />,
                  endAdornment: searchTerm && (
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon style={{ color: "var(--v2-text)" }} fontSize="small" />
                    </IconButton>
                  )
                }}
              />
            </Box>
          </Box>

          {viewMode === "table" ? (
            <Box sx={{ height: 500 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                style={{ color: "var(--v2-text)" }}
                pageSize={7}
                rowsPerPageOptions={[7, 14, 21]}
                disableSelectionOnClick
                onRowClick={(params) => handleTenantClick(params.row)}
                sx={{
                  backgroundColor: "var(--v2-surface)",
                  color: "var(--v2-text)",
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: "var(--v2-surface)",
                    color: "var(--v2-text)",
                    borderBottom: `1px solid ${"var(--v2-border)"}`,
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: "var(--v2-surface)",
                    color: "var(--v2-text)",
                    '&:focus': {
                      outline: 'none',
                    },
                    '&:focus-within': {
                      outline: 'none',
                    },
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    color: "var(--v2-text)",
                  },
                  '& .MuiDataGrid-columnHeaderTitleContainer': {
                    color: "var(--v2-text)",
                  },
                  '& .MuiDataGrid-iconButtonContainer': {
                    visibility: 'visible',
                    '& .MuiIconButton-root': {
                      color: "var(--v2-text)",
                    },
                  },
                  '& .MuiDataGrid-sortIcon': {
                    color: "var(--v2-text)",
                  },
                  '& .MuiDataGrid-menuIconButton': {
                    color: "var(--v2-text)",
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: "1px solid var(--v2-border)",
                    color: "var(--v2-text)",
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: "var(--v2-accent-subtle)",
                    cursor: 'pointer'
                  },
                  '& .MuiTablePagination-root': {
                    color: "var(--v2-text)",
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    color: "var(--v2-text)",
                  },
                  '& .MuiIconButton-root': {
                    color: "var(--v2-text)",
                  },
                }}
              />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredTenants.map((tenant, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: "var(--v2-bg)",
                      border: "1px solid var(--v2-border)",
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                    onClick={() => handleTenantClick(tenant)}
                  >
                    <CardHeader
                      title={tenant.name}
                      style={{ color: "var(--v2-text)" }}
                      subheader={`Created: ${tenant.created_at.split('T')[0]}`}
                      titleTypographyProps={{ variant: 'h6' }}
                      action={
                        <Box display="flex" gap={1} alignItems="center">
                          <Chip
                            label={tenant.status}
                            size="small"
                            color={tenant.status === 'active' ? 'success' : tenant.status === 'disabled' ? 'warning' : 'default'}
                            variant="outlined"
                          />
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      }
                    />
                    <Divider />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box style={{ color: "var(--v2-text)" }} sx={{ mb: 2 }}>
                        <Typography style={{ color: "var(--v2-text)" }} variant="body2" color="textSecondary" gutterBottom>
                          Domain
                        </Typography>
                        <Typography style={{ color: "var(--v2-text)" }} variant="body1" sx={{ wordBreak: 'break-all' }}>
                          {tenant.domain}
                        </Typography>
                      </Box>

                      <Box display="flex" gap={2} flexWrap="wrap">
                        <Box
                          style={{ color: "var(--v2-text)" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTenantClick(tenant);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Typography style={{ color: "var(--v2-text)" }} variant="body2" color="textSecondary" gutterBottom>
                            Users
                          </Typography>
                          <Chip
                            label={tenant.users_count || 0}
                            color="primary"
                            size="small"
                            icon={<PeopleIcon style={{ color: "var(--v2-text)" }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTenantClick(tenant);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Box>
                        <Box style={{ color: "var(--v2-text)" }}>
                          <Typography style={{ color: "var(--v2-text)" }} variant="body2" color="textSecondary" gutterBottom>
                            Organizations
                          </Typography>
                          <Chip
                            label={tenant.organizations_count || 0}
                            color="secondary"
                            size="small"
                            icon={<BusinessIcon style={{ color: "var(--v2-text)" }} />}
                          />
                        </Box>
                        <Box>
                          <Typography style={{ color: "var(--v2-text)" }} variant="body2" color="textSecondary" gutterBottom>
                            Files
                          </Typography>
                          <Chip
                            label={tenant.files_count || 0}
                            color="default"
                            size="small"
                            icon={<FolderIcon style={{ color: 'blue' }} />}
                          />
                        </Box>
                        <Box>
                          <Typography style={{ color: "var(--v2-text)" }} variant="body2" color="textSecondary" gutterBottom>
                            License
                          </Typography>
                          <Chip
                            label={tenantLicenseLabel(tenant)}
                            color={tenant.license_type === 'pro' ? (tenant.license_source === 'trial' ? 'warning' : 'primary') : 'default'}
                            size="small"
                            icon={<WorkspacePremiumIcon style={{ color: (tenant.license_type || 'community') === 'pro' ? 'gold' : 'gray' }} />}
                            variant={(tenant.license_type || 'community') === 'pro' ? 'filled' : 'outlined'}
                          />
                        </Box>
                      </Box>

                      <Box display="flex" gap={1} mt={2} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={() => openUpdateLicenseDialog(tenant)}
                          title="Update License"
                          color="primary"
                        >
                          <WorkspacePremiumIcon fontSize="small" />
                        </IconButton>
                        {tenant.status === 'active' && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => openActionDialog(tenant, 'disable')}
                              title="Disable"
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => openActionDialog(tenant, 'archive')}
                              title="Archive"
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        {tenant.status === 'disabled' && (
                          <IconButton
                            size="small"
                            onClick={() => openActionDialog(tenant, 'enable')}
                            title="Enable"
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => openActionDialog(tenant, 'delete')}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Card>
      )}

      {/* Create Tenant Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--v2-surface)",
            color: "var(--v2-text)",
          }
        }}
      >
        <DialogTitle sx={{ color: "var(--v2-text)" }}>Create New Tenant</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Tenant Name"
              value={newTenant.tenant_name}
              onChange={(e) => setNewTenant({ ...newTenant, tenant_name: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <TextField
              label="Domain"
              value={newTenant.tenant_domain}
              onChange={(e) => setNewTenant({ ...newTenant, tenant_domain: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <TextField
              label="Address"
              value={newTenant.address}
              onChange={(e) => setNewTenant({ ...newTenant, address: e.target.value })}
              fullWidth
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <TextField
              label="Website"
              value={newTenant.website}
              onChange={(e) => setNewTenant({ ...newTenant, website: e.target.value })}
              fullWidth
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <TextField
              label="Logo URL"
              value={newTenant.logo_url}
              onChange={(e) => setNewTenant({ ...newTenant, logo_url: e.target.value })}
              fullWidth
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ color: "var(--v2-text-secondary)", '&.Mui-focused': { color: "var(--v2-text)" } }}>Status</InputLabel>
              <Select
                value={newTenant.status}
                onChange={(e) => setNewTenant({ ...newTenant, status: e.target.value })}
                label="Status"
                sx={{
                  color: "var(--v2-text)",
                  '& .MuiSvgIcon-root': { color: "var(--v2-text)" },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-border)"
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-border-strong)"
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-text)"
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "var(--v2-bg)",
                      '& .MuiMenuItem-root': {
                        color: "var(--v2-text)",
                        '&:hover': {
                          backgroundColor: "var(--v2-sunken)"
                        },
                        '&.Mui-selected': {
                          backgroundColor: "var(--v2-accent-subtle)"
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "var(--v2-surface)" }}>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ color: "var(--v2-text)" }}>Cancel</Button>
          <Button
            onClick={handleCreateTenant}
            variant="contained"
            disabled={!newTenant.tenant_name || !newTenant.tenant_domain}
          >
            Create Tenant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "var(--v2-surface)",
            color: "var(--v2-text)",
          }
        }}
      >
        <DialogTitle sx={{ color: "var(--v2-text)" }}>
          {actionType === 'delete' && 'Delete Tenant'}
          {actionType === 'archive' && 'Archive Tenant'}
          {actionType === 'disable' && 'Disable Tenant'}
          {actionType === 'enable' && 'Enable Tenant'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "var(--v2-text)" }}>
            {actionType === 'delete' && `Are you sure you want to delete "${selectedTenant?.name}"? This action cannot be undone and will remove all associated data.`}
            {actionType === 'archive' && `Are you sure you want to archive "${selectedTenant?.name}"?`}
            {actionType === 'disable' && `Are you sure you want to disable "${selectedTenant?.name}"?`}
            {actionType === 'enable' && `Are you sure you want to enable "${selectedTenant?.name}"?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "var(--v2-surface)" }}>
          <Button onClick={() => setActionDialogOpen(false)} sx={{ color: "var(--v2-text)" }}>Cancel</Button>
          <Button
            onClick={() => handleTenantAction(selectedTenant?.id, actionType)}
            variant="contained"
            color={actionType === 'delete' ? 'error' : 'primary'}
          >
            {actionType === 'delete' && 'Delete'}
            {actionType === 'archive' && 'Archive'}
            {actionType === 'disable' && 'Disable'}
            {actionType === 'enable' && 'Enable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog
        open={createUserDialogOpen}
        onClose={() => setCreateUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--v2-surface)",
            color: "var(--v2-text)",
          }
        }}
      >
        <DialogTitle sx={{ color: "var(--v2-text)" }}>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Username"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <TextField
              label="Email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: "var(--v2-text)" }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <TextField
              label="Contact Number"
              value={newUser.contact_number}
              onChange={(e) => setNewUser({ ...newUser, contact_number: e.target.value })}
              fullWidth
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ color: "var(--v2-text-secondary)", '&.Mui-focused': { color: "var(--v2-text)" } }}>Role</InputLabel>
              <Select
                value={newUser.is_admin ? 'admin' : 'user'}
                onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.value === 'admin' })}
                label="Role"
                sx={{
                  color: "var(--v2-text)",
                  '& .MuiSvgIcon-root': { color: "var(--v2-text)" },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-border)"
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-border-strong)"
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-text)"
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "var(--v2-bg)",
                      '& .MuiMenuItem-root': {
                        color: "var(--v2-text)",
                        '&:hover': {
                          backgroundColor: "var(--v2-sunken)"
                        },
                        '&.Mui-selected': {
                          backgroundColor: "var(--v2-accent-subtle)"
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "var(--v2-surface)" }}>
          <Button onClick={() => setCreateUserDialogOpen(false)} sx={{ color: "var(--v2-text)" }}>Cancel</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={!newUser.username || !newUser.email || !newUser.password || createUserLoading}
          >
            {createUserLoading ? <CircularProgress size={20} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update License Dialog */}
      <Dialog
        open={updateLicenseDialogOpen}
        onClose={() => {
          setUpdateLicenseDialogOpen(false);
          setSelectedTenantForLicense(null);
          setLicenseType('community');
          setLicenseExpiresAt('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--v2-surface)",
            color: "var(--v2-text)",
          }
        }}
      >
        <DialogTitle sx={{ color: "var(--v2-text)" }}>
          Update License - {selectedTenantForLicense?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Tenant"
              value={selectedTenantForLicense?.name || ''}
              fullWidth
              disabled
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ color: "var(--v2-text-secondary)", '&.Mui-focused': { color: "var(--v2-text)" } }}>License Type</InputLabel>
              <Select
                value={licenseType}
                onChange={(e) => {
                  setLicenseType(e.target.value);
                  if (e.target.value === 'community') {
                    setLicenseExpiresAt('');
                  }
                }}
                label="License Type"
                sx={{
                  color: "var(--v2-text)",
                  '& .MuiSvgIcon-root': { color: "var(--v2-text)" },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-border)"
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-border-strong)"
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-text)"
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "var(--v2-bg)",
                      '& .MuiMenuItem-root': {
                        color: "var(--v2-text)",
                        '&:hover': {
                          backgroundColor: "var(--v2-sunken)"
                        },
                        '&.Mui-selected': {
                          backgroundColor: "var(--v2-accent-subtle)"
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="community">Community</MenuItem>
                <MenuItem value="pro">Pro</MenuItem>
              </Select>
            </FormControl>
            {licenseType === 'pro' && (
              <TextField
                label="Expiration Date"
                type="date"
                value={licenseExpiresAt}
                onChange={(e) => setLicenseExpiresAt(e.target.value)}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                  sx: { color: "var(--v2-text-secondary)" }
                }}
                InputProps={{
                  startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: "var(--v2-text-secondary)" }} />
                }}
                sx={{
                  '& .MuiInputBase-input': { color: "var(--v2-text)" },
                  '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                  '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-border)"
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-border-strong)"
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-text)"
                  },
                  '& .MuiFormHelperText-root': { color: "var(--v2-text-secondary)" }
                }}
                helperText="Select when the Pro license expires"
              />
            )}
            {selectedTenantForLicense?.license_expires_at && (
              <Typography
                variant="body2"
                sx={{ color: "var(--v2-text-secondary)" }}
              >
                Current expiration: {new Date(selectedTenantForLicense.license_expires_at).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "var(--v2-surface)" }}>
          <Button
            onClick={() => {
              setUpdateLicenseDialogOpen(false);
              setSelectedTenantForLicense(null);
              setLicenseType('community');
              setLicenseExpiresAt('');
            }}
            sx={{ color: "var(--v2-text)" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateLicense}
            variant="contained"
            disabled={(licenseType === 'pro' && !licenseExpiresAt) || updateLicenseLoading}
            color="primary"
          >
            {updateLicenseLoading ? <CircularProgress size={20} /> : 'Update License'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Password Dialog */}
      <Dialog
        open={updatePasswordDialogOpen}
        onClose={() => {
          setUpdatePasswordDialogOpen(false);
          setSelectedUserForPassword(null);
          setNewPassword('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--v2-surface)",
            color: "var(--v2-text)",
          }
        }}
      >
        <DialogTitle sx={{ color: "var(--v2-text)" }}>
          Update Password - {selectedUserForPassword?.username}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Email"
              value={selectedUserForPassword?.email || ''}
              fullWidth
              disabled
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" }
              }}
            />
            <TextField
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    sx={{ color: "var(--v2-text)" }}
                  >
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiFormHelperText-root': { color: "var(--v2-text-secondary)" }
              }}
              helperText="Password must be at least 6 characters"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "var(--v2-surface)" }}>
          <Button
            onClick={() => {
              setUpdatePasswordDialogOpen(false);
              setSelectedUserForPassword(null);
              setNewPassword('');
            }}
            sx={{ color: "var(--v2-text)" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdatePassword}
            variant="contained"
            disabled={!newPassword || newPassword.length < 6 || updatePasswordLoading}
            color="primary"
          >
            {updatePasswordLoading ? <CircularProgress size={20} /> : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editUserDialogOpen}
        onClose={() => {
          setEditUserDialogOpen(false);
          setEditingUser(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--v2-surface)",
            color: "var(--v2-text)",
          }
        }}
      >
        <DialogTitle sx={{ color: "var(--v2-text)" }}>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Username"
              value={editingUser?.username || ''}
              onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <TextField
              label="Email"
              type="email"
              value={editingUser?.email || ''}
              onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <TextField
              label="Contact Number"
              value={editingUser?.contact_number || ''}
              onChange={(e) => setEditingUser({ ...editingUser, contact_number: e.target.value })}
              fullWidth
              sx={{
                '& .MuiInputBase-input': { color: "var(--v2-text)" },
                '& .MuiInputLabel-root': { color: "var(--v2-text-secondary)" },
                '& .MuiInputLabel-root.Mui-focused': { color: "var(--v2-text)" },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border)"
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-border-strong)"
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: "var(--v2-text)"
                }
              }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ color: "var(--v2-text-secondary)", '&.Mui-focused': { color: "var(--v2-text)" } }}>Role</InputLabel>
              <Select
                value={editingUser?.is_admin ? 'admin' : 'user'}
                onChange={(e) => setEditingUser({ ...editingUser, is_admin: e.target.value === 'admin' })}
                label="Role"
                sx={{
                  color: "var(--v2-text)",
                  '& .MuiSvgIcon-root': { color: "var(--v2-text)" },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-border)"
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-border-strong)"
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: "var(--v2-text)"
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "var(--v2-bg)",
                      '& .MuiMenuItem-root': {
                        color: "var(--v2-text)",
                        '&:hover': {
                          backgroundColor: "var(--v2-sunken)"
                        },
                        '&.Mui-selected': {
                          backgroundColor: "var(--v2-accent-subtle)"
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "var(--v2-surface)" }}>
          <Button
            onClick={() => {
              setEditUserDialogOpen(false);
              setEditingUser(null);
            }}
            sx={{ color: "var(--v2-text)" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveUserChanges}
            variant="contained"
            disabled={!editingUser?.username || !editingUser?.email || editUserLoading}
          >
            {editUserLoading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

function TenantManagement() {
  const [tab, setTab] = useState('overview');
  return <>
    <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="Tenant management sections" sx={{ mb: 2 }}>
      <Tab value="overview" label="Overview" id="tenant-overview-tab" aria-controls="tenant-overview-panel" />
      <Tab value="engagement" label="Engagement" id="tenant-engagement-tab" aria-controls="tenant-engagement-panel" />
    </Tabs>
    <div role="tabpanel" id={`tenant-${tab}-panel`} aria-labelledby={`tenant-${tab}-tab`}>
      {tab === 'overview' ? <TenantChart /> : <TenantEngagement />}
    </div>
  </>;
}
export default TenantManagement;
